import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { environment } from '@environments/environment';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { ServerReachabilityService } from '../services/network/server-reachability.service';
import {
    isApiHealthCheckUrl,
    isApiOriginUrl,
    isHttpNetworkError
} from '../services/network/server-reachability.util';
import { AppStore } from '../services/store/app.store';

/**
 * Aggiorna lo stato reachability per richieste verso origin `API_ENDPOINT`.
 * Vedi `docs/NETWORK_CONNECTIVITY.md`.
 */
function trackApiReachability<T>(
  source$: Observable<T>,
  requestUrl: string,
  serverReachability: ServerReachabilityService
): Observable<T> {
  if (!isApiOriginUrl(requestUrl, environment.API_ENDPOINT)) {
    return source$;
  }

  return source$.pipe(
    tap(() => serverReachability.markReachable()),
    catchError((error: { status?: number; error?: unknown; name?: string }) => {
      if (isHttpNetworkError(error)) {
        serverReachability.markUnreachable();
      } else if (typeof error.status === 'number' && error.status > 0) {
        serverReachability.markReachable();
      }
      return throwError(() => error);
    })
  );
}

/**
 * Interceptor HTTP per la gestione automatica del refresh token su errori 401.
 *
 * Comportamento:
 * - Se rememberMe = false: logout -> login
 * - Se rememberMe = true: refresh token e riprova la richiesta
 *
 * Evita loop infiniti non facendo refresh su endpoint di autenticazione:
 * - /refresh-token
 * - /login
 * - /auth/logout
 * - /up (health check connectivity)
 */
let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<{ success: boolean; error?: unknown } | null>(null);

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const authService = inject(AuthService);
  const appStore = inject(AppStore);
  const serverReachability = inject(ServerReachabilityService);

  const isHealthCheck = isApiHealthCheckUrl(req.url, environment.API_ENDPOINT);

  return trackApiReachability(
    next(req).pipe(
      catchError((error) => {
        const statusCode = (error as { status_code?: number; status?: number })?.status_code
          ?? (error as { status?: number })?.status;

        if (statusCode === 401) {
          const url = req.url.toLowerCase();
          if (
            isHealthCheck ||
            url.includes('/refresh-token') ||
            url.includes('/login') ||
            url.includes('/logout')
          ) {
            return throwError(() => error);
          }

          const token = appStore.token();
          const rememberMe = appStore.rememberMe();

          if (!token || !token.refresh_token) {
            authService.logout().subscribe();
            return throwError(() => error);
          }

          if (!rememberMe) {
            authService.logout().subscribe();
            return throwError(() => error);
          }

          if (isRefreshing) {
            return refreshTokenSubject.pipe(
              filter((result) => result !== null),
              take(1),
              switchMap((result) => {
                if (result && !result.success) {
                  return throwError(() => result.error || error);
                }
                const newToken = appStore.token();
                if (newToken?.token) {
                  const clonedReq = req.clone({
                    setHeaders: {
                      Authorization: `Bearer ${newToken.token}`
                    }
                  });
                  return next(clonedReq);
                }
                authService.logout().subscribe();
                return throwError(() => error);
              })
            );
          }

          isRefreshing = true;
          refreshTokenSubject.next(null);

          return authService.refreshToken().pipe(
            switchMap(() => {
              isRefreshing = false;
              refreshTokenSubject.next({ success: true });
              const newToken = appStore.token();
              if (newToken?.token) {
                const clonedReq = req.clone({
                  setHeaders: {
                    Authorization: `Bearer ${newToken.token}`
                  }
                });
                return next(clonedReq);
              }
              authService.logout().subscribe();
              return throwError(() => error);
            }),
            catchError((refreshError) => {
              isRefreshing = false;
              refreshTokenSubject.next({ success: false, error: refreshError });
              return throwError(() => refreshError);
            })
          );
        }

        return throwError(() => error);
      })
    ),
    req.url,
    serverReachability
  );
};
